import graphql__89436d2199958791ebd4 from "./__generated__/SendMarketingEmailTestEmailDialogContactsQuery.graphql.ts";
import graphql__e9df1161863c8e9958b2 from "./__generated__/SendMarketingEmailTestEmailDialog_marketingEmail.graphql.ts";
import graphql__a946554aba3f7695f1c5 from "./__generated__/SendMarketingEmailTestEmailDialogMeQuery.graphql.ts";
import graphql__8b4c2de4670d63ef8422 from "./__generated__/SendMarketingEmailTestEmailDialogFindContactQuery.graphql.ts";
import graphql__02c6d33c512e71d6312d from "./__generated__/SendMarketingEmailTestEmailDialogMutation.graphql.ts";import * as React from "react";
import {graphql, fetchQuery} from "react-relay";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Field from "../form/Field";
import Row from "../form/Row";
import Label from "../form/Label";
import * as yup from "yup";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";
import {useIntl} from "react-intl";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import asFormikField from "../abstract/asFormikField";
import AbstractCombobox from "../abstract/AbstractCombobox";
import type {SendMarketingEmailTestEmailDialogMutation} from "./__generated__/SendMarketingEmailTestEmailDialogMutation.graphql";

type Props = {
    queryRef: any,
    handleCloseDialog: () => void
}

const contactsDataProvider = (relayEnvironment) => {
    return AwesomeDebouncePromise((searchTerm: string = '') => {
        return fetchQuery(relayEnvironment, graphql__89436d2199958791ebd4, {searchTerm}).toPromise().then((data) => {
            if (!data) {
                return [];
            }
            //@ts-ignore
            return data.contacts?.edges?.map((edge) => edge.node) || [];
        });
    }, 500);
}

const getContactLabel = (contact) => {
    if (!contact) return '';
    if (contact.__typename === 'TeamContact') {
        return contact.teamName || '';
    }
    return [contact.givenName, contact.familyName].filter(Boolean).join(' ') || '';
};

const getContactSecondaryLabel = (contact) => {
    if (!contact) return '';
    return contact.emailAddress || '';
};

const SendMarketingEmailTestEmailDialog: React.FC<Props> = ({queryRef, handleCloseDialog}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const [isLoading, setIsLoading] = React.useState(true);
    const [currentUserEmail, setCurrentUserEmail] = React.useState('');
    const [defaultContact, setDefaultContact] = React.useState<any>(null);

    const marketingEmail = useFragment(
        graphql__e9df1161863c8e9958b2,
        queryRef
    );

    // Load current user and try to find matching contact
    React.useEffect(() => {
        const loadInitialData = async () => {
            try {
                // First get current user
                const meData: any = await fetchQuery(relayEnvironment, graphql__a946554aba3f7695f1c5, {}).toPromise();

                const userEmail = meData?.me?.email || '';
                setCurrentUserEmail(userEmail);

                // Try to find a contact with this email
                if (userEmail) {
                    const contactData: any = await fetchQuery(relayEnvironment, graphql__8b4c2de4670d63ef8422, {searchTerm: userEmail}).toPromise();

                    const foundContact = contactData?.contacts?.edges?.[0]?.node;
                    if (foundContact && foundContact.emailAddress?.toLowerCase() === userEmail.toLowerCase()) {
                        setDefaultContact(foundContact);
                    }
                }
            } catch (error) {
                console.error('Failed to load initial data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, [relayEnvironment]);

    const handleSubmission = (values) => {
        commitMutationWithErrorHandling<SendMarketingEmailTestEmailDialogMutation>(
            relayEnvironment,
            {
                mutation: graphql__02c6d33c512e71d6312d,
                variables: {
                    input: {
                        id: marketingEmail.id,
                        recipientEmail: values.recipientEmail,
                        sampleContactIdentifier: values.sampleContact?.id || null
                    }
                },
                onCompleted: () => {
                    handleCloseDialog();
                },
                onError: (error) => {
                    console.error(error);
                    handleCloseDialog();
                }
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToSendTestEmail'})
            }
        )
    }

    const SampleContactCombobox = asFormikField(AbstractCombobox, 'sampleContact');

    if (isLoading) {
        return (
            <FormDialog
                handleCloseDialog={handleCloseDialog}
                handleSubmission={() => {}}
                initialValues={{recipientEmail: '', sampleContact: null}}
                validationSchema={yup.object()}
                headline={intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.headline'})}
                submitLabel={intl.formatMessage({id: 'button.sendTestEmail'})}
            >
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
            </FormDialog>
        );
    }

    const initialValues = {
        recipientEmail: currentUserEmail,
        sampleContact: defaultContact
    }

    const validationSchema = yup.object().shape({
        recipientEmail: yup.string()
            .email(intl.formatMessage({id: 'validation.field.invalidEmail'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    return (
        <FormDialog
            handleCloseDialog={handleCloseDialog}
            handleSubmission={handleSubmission}
            initialValues={initialValues}
            validationSchema={validationSchema}
            headline={intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.headline'})}
            submitLabel={intl.formatMessage({id: 'button.sendTestEmail'})}
        >
            <p className="text-gray-600 mb-4">
                {intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.description'}, {name: marketingEmail.name})}
            </p>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.recipientEmailLabel'})}</Label>
                    <Field name="recipientEmail" type="email" autoFocus={true} placeholder="test@example.com"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.sampleContactLabel'})}</Label>
                    <SampleContactCombobox
                        getEntryId={(contact) => contact.id}
                        getEntryLabel={getContactLabel}
                        getEntrySecondaryLabel={getContactSecondaryLabel}
                        dataProvider={contactsDataProvider(relayEnvironment)}
                        placeholder={intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.sampleContactPlaceholder'})}
                    />
                </Row>
            </Section>
            <p className="text-sm text-gray-500 mt-4">
                {intl.formatMessage({id: 'dialog.SendMarketingEmailTestEmailDialog.sampleContactHint'})}
            </p>
        </FormDialog>
    )
}

export default SendMarketingEmailTestEmailDialog;
